import { useEffect, useMemo, useRef, useState } from "react";

function getPoint(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  const source = event.touches?.[0] || event.changedTouches?.[0] || event;
  return {
    x: ((source.clientX - rect.left) / rect.width) * canvas.width,
    y: ((source.clientY - rect.top) / rect.height) * canvas.height,
  };
}

export default function SignaturePad({
  title = "Firma manuscrita",
  description = "Dibuja tu firma dentro del recuadro.",
  width = 320,
  height = 120,
  onConfirm,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [previewUrl, setPreviewUrl] = useState("");

  const canvasSize = useMemo(() => ({ width, height }), [width, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvasSize.width * ratio;
    canvas.height = canvasSize.height * ratio;
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;

    const context = canvas.getContext("2d");
    context.scale(ratio, ratio);
    context.lineJoin = "round";
    context.lineCap = "round";
    context.strokeStyle = "#142626";
    context.lineWidth = 2.4;
    context.clearRect(0, 0, canvasSize.width, canvasSize.height);

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [canvasSize.height, canvasSize.width, previewUrl]);

  function startDrawing(event) {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    const point = getPoint(event, canvas);
    drawingRef.current = true;
    context.beginPath();
    context.moveTo(point.x / (window.devicePixelRatio || 1), point.y / (window.devicePixelRatio || 1));
    event.preventDefault();
  }

  function draw(event) {
    if (!drawingRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    const point = getPoint(event, canvas);
    context.lineTo(point.x / (window.devicePixelRatio || 1), point.y / (window.devicePixelRatio || 1));
    context.stroke();
    hasStrokeRef.current = true;
    setIsEmpty(false);
    event.preventDefault();
  }

  function stopDrawing(event) {
    if (!drawingRef.current) {
      return;
    }
    drawingRef.current = false;
    event.preventDefault();
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvasSize.width, canvasSize.height);
    hasStrokeRef.current = false;
    setIsEmpty(true);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  }

  function confirmSignature() {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokeRef.current) {
      return;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        const nextPreview = URL.createObjectURL(blob);
        setPreviewUrl(nextPreview);
        const reader = new FileReader();
        reader.onloadend = () => {
          onConfirm?.({
            signature_image_base64: reader.result,
            width: canvasSize.width,
            height: canvasSize.height,
          });
        };
        reader.readAsDataURL(blob);
      },
      "image/png",
      1
    );
  }

  return (
    <section className="detail-panel signature-pad-card">
      <div className="detail-panel-header">
        <h2>{title}</h2>
      </div>
      <div className="panel-body signature-pad-body">
        <p>{description}</p>
        {!previewUrl ? (
          <>
            <div className="signature-pad-stage">
              <canvas
                ref={canvasRef}
                className="signature-pad-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <div className="signature-pad-actions">
              <button className="secondary-button" type="button" onClick={clearSignature}>
                Limpiar
              </button>
              <button className="submit-button" type="button" onClick={confirmSignature} disabled={isEmpty}>
                Confirmar firma
              </button>
            </div>
          </>
        ) : null}
        {previewUrl ? (
          <div className="signature-pad-preview">
            <img src={previewUrl} alt="Firma capturada" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
